import { useCallback, useState, useRef } from 'react'
import classNames from 'classnames'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form/dist/index.ie11'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheck } from '@fortawesome/pro-regular-svg-icons'
import formatError from '../../../utilities/formatError'
import { formatPercentage } from '../utils'
import { Stack } from '../../stack'
import { Button } from '../../button'
import { TextField } from '../../textfield'
import { Select } from '../../select'
import { Alert } from '../../alert'
import { Row, Col } from '../../grid'
import { Heading } from '../../typography/Heading'
import computeOffsetTop from '../../../utilities/computeTopOffset'
import useEnabledOnClient from '../../../utilities/useEnabledOnClient'
import * as styles from '../CalculatorModule.module.scss'

// allowable loan terms (in years)
const LOAN_TERMS = [10, 15, 20, 25, 30]

const validationSchema = {
  loanAmount: { min: 0 },
  interestRate: {
    min: 0,
    max: 40,
  },
  currentTerm: {
    min: 1,
    max: 360,
  },
  refinanceRate: {
    min: 0,
    max: 40,
  },
  refinanceTerm: {
    min: 1,
    max: 360,
  },
  originationFee: {
    min: 0,
    max: 5,
  },
  discountPoints: {
    min: 0,
    max: 5,
  },
  refinanceFees: {
    min: 0,
    max: 999999999,
  },
}

const RefinanceCalculator = () => {
  const [t, i18n] = useTranslation()
  const [language, countryCode] = i18n.language.split('-')
  const [calcResults, setCalcResults] = useState()

  const { register, errors, handleSubmit } = useForm({
    mode: 'onBlur',
    defaultValues: {
      loanAmount: 0,
      interestRate: 5,
      currentTerm: 360,
      refinanceRate: 3.5,
      refinanceTerm: 360,
      originationFee: 1,
      discountPoints: 0,
      refinanceFees: 2500,
    },
  })

  const [inProgress, setInProgress] = useState(false)
  const [alert, showAlert] = useState(null)
  const dismissAlert = useCallback(() => showAlert(null), [])
  const resultsContainer = useRef(null)
  const resultsHeader = useRef(null)

  // Our API doesn’t support sync form submissions and so we need Javascript to submit the form asynchronously by issuing an XHR. Therefore, we need to ensure that the button is initially disabled and gets re-enabled once all Javascript has loaded on the client.
  const disabled = useEnabledOnClient()

  const handleSubmitValid = async formData => {
    setInProgress(true)
    showAlert(false)

    try {
      const url = 'https://www.calcxml.com/rest/hom02'

      // Documentation
      // https://www.calcxml.com/documentation/rest/Hom02JSONRequest.htm

      const payload = {
        username: process.env.CALCXML_USERNAME,
        password: process.env.CALCXML_PASSWORD,
        language,
        countryCode,
        chartWidth: 900,
        chartHeight: 400,
        barColor1: '63B5E4',
        barColor2: '84BD00',
        barColor3: 'FCC841',
        // barColor4: '#ffffff',
        // barColor5: '#ffffff',
        // barColor6: '#ffffff',
        loanAmount: formData.loanAmount,
        interestRate: formatPercentage(formData.interestRate),
        currentTerm: formData.currentTerm,
        refinanceRate: formatPercentage(formData.refinanceRate),
        refinanceTerm: formData.refinanceTerm,
        originationFee: formatPercentage(formData.originationFee),
        discountPoints: formatPercentage(formData.discountPoints),
        refinanceFees: formData.refinanceFees,
      }

      const response = await axios.post(url, payload)

      if (response.status !== 200 || Boolean(response.data.error)) {
        throw new Error(`Unexpected status code: ${response.status} ${response.statusText}`)
      }

      setInProgress(false)
      setCalcResults(response.data)
      window.scrollTo({
        top: computeOffsetTop(resultsContainer.current) - 20,
        behavior: 'smooth',
      })
      resultsHeader.current.focus()
    } catch (error) {
      setInProgress(false)
      showAlert({
        type: 'error',
        title: t('RefinanceCalculator.error', {
          defaultValue: 'Error',
        }),
        message: error.message,
      })
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  return (
    <div>
      {alert && (
        <Row>
          <Col xs={12}>
            <Alert variant={alert.type} title={alert.title} onDismiss={dismissAlert}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}
      <form
        action="/"
        method="POST"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleSubmitValid)}
        className={alert ? styles.HasAlert : ''}
      >
        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('RefinanceCalculator.firstLegend', {
              defaultValue: 'Current loan information',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RefinanceCalculator.currentLoanBalanceLabel', {
                  defaultValue: 'Current loan balance ($)',
                })}
                id="loanAmount"
                name="loanAmount"
                type="number"
                ref={register(validationSchema.loanAmount)}
                error={formatError(errors.loanAmount, validationSchema.loanAmount, t)}
                autoFocus
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <TextField
                label={t('RefinanceCalculator.annualInterestRateLabel', {
                  defaultValue: 'Annual interest rate (%)',
                })}
                id="interestRate"
                name="interestRate"
                type="number"
                min="0"
                max="40"
                ref={register(validationSchema.interestRate)}
                error={formatError(errors.interestRate, validationSchema.interestRate, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RefinanceCalculator.numberOfmonthsRemainingLabel', {
                  defaultValue: 'Number of months remaining (1 to 360)',
                })}
                id="currentTerm"
                name="currentTerm"
                type="number"
                min="1"
                max="360"
                ref={register(validationSchema.currentTerm)}
                error={formatError(errors.currentTerm, validationSchema.currentTerm, t)}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('RefinanceCalculator.secondLegend', {
              defaultValue: 'Proposed loan information',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RefinanceCalculator.annualInterestRateMortgageLabel', {
                  defaultValue: 'Annual interest rate on new mortgage (%)',
                })}
                id="refinanceRate"
                name="refinanceRate"
                type="number"
                min="0"
                max="40"
                ref={register(validationSchema.refinanceRate)}
                error={formatError(errors.refinanceRate, validationSchema.refinanceRate, t)}
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <Select
                label={t('RefinanceCalculator.numberOfMonthsLabel', {
                  defaultValue: 'Loan term (1 to 360 months)',
                })}
                id="refinanceTerm"
                name="refinanceTerm"
                ref={register(validationSchema.refinanceTerm)}
                error={formatError(errors.refinanceTerm, validationSchema.refinanceTerm, t)}
              >
                {LOAN_TERMS.map(years => (
                  <Select.Option value={years * 12}>{years}</Select.Option>
                ))}
              </Select>
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RefinanceCalculator.loanOriginationFee', {
                  defaultValue: 'Loan origination fee (0% to 5%)',
                })}
                id="originationFee"
                name="originationFee"
                type="number"
                min="0"
                max="5"
                ref={register(validationSchema.originationFee)}
                error={formatError(errors.originationFee, validationSchema.originationFee, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RefinanceCalculator.otherFeesDiscountPointsLabel', {
                  defaultValue: 'Other fees/discount points (0% to 5%)',
                })}
                id="discountPoints"
                name="discountPoints"
                type="number"
                min="0"
                ref={register(validationSchema.discountPoints)}
                error={formatError(errors.discountPoints, validationSchema.discountPoints, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('RefinanceCalculator.otherFeesLabel', {
                  defaultValue: 'Other fees ($)',
                })}
                id="refinanceFees"
                name="refinanceFees"
                type="number"
                min="0"
                ref={register(validationSchema.refinanceFees)}
                error={formatError(errors.refinanceFees, validationSchema.refinanceFees, t)}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <Stack alignment="center" className={styles.Submit}>
          <Button
            type="submit"
            disabled={inProgress || disabled}
            loading={inProgress}
            alt={t('RefinanceCalculator.calculateAlt', {
              defaultValue: 'Calculate to view results',
            })}
          >
            {t('RefinanceCalculator.calculate', {
              defaultValue: 'Calculate',
            })}
          </Button>

          {alert && alert.type === 'error' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faTimes} className={styles.TimesIcon} />
              {t('RefinanceCalculator.couldNotCalculate', {
                defaultValue: 'Sorry, could not calculate',
              })}
            </span>
          )}

          {alert && alert.type === 'success' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faCheck} className={styles.CheckIcon} />
              {t('RefinanceCalculator.calculationComplete', {
                defaultValue: 'Calculation complete. See below for results!',
              })}
            </span>
          )}
        </Stack>
      </form>

      <div ref={resultsContainer} />

      {calcResults && (
        <div className={styles.Results}>
          <Heading
            className={styles.ResultsHeading}
            element="h3"
            size={3}
            tabIndex="0"
            forwardedRef={resultsHeader}
          >
            {t('RefinanceCalculator.resutls', {
              defaultValue: 'Results',
            })}
          </Heading>
          <p>{calcResults.responseText}</p>
          <p>{calcResults.responseText2}</p>
          <p>{calcResults.responseText3}</p>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('RefinanceCalculator.summaryTable', {
              defaultValue: 'Summary table',
            })}
          </Heading>

          <table className={styles.ResultsTable} role="table">
            <caption>
              {t('RefinanceCalculator.summaryTable', {
                defaultValue: 'Summary table',
              })}
            </caption>
            <tbody>
              <tr>
                <th aria-label="No value" />
                <th scope="col">
                  {t('RefinanceCalculator.currentMortgage', {
                    defaultValue: 'Current mortgage',
                  })}
                </th>
                <th scope="col">
                  {t('RefinanceCalculator.traditionalRefinance', {
                    defaultValue: 'Traditional refinance',
                  })}
                </th>
                <th scope="col">
                  {t('RefinanceCalculator.enhancedRefinance', {
                    defaultValue: 'Enhanced refinance',
                  })}
                </th>
              </tr>
              <tr>
                <th scope="row">
                  {t('RefinanceCalculator.term', {
                    defaultValue: 'Term',
                  })}
                </th>
                <td aria-atomic="true">{calcResults.currentTerm0}</td>
                <td aria-atomic="true">{calcResults.traditionalTerm}</td>
                <td aria-atomic="true">{calcResults.enhancedTerm}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RefinanceCalculator.payment', {
                    defaultValue: 'Payment',
                  })}
                </th>
                <td>{calcResults.currentPayment}</td>
                <td>{calcResults.traditionalPayment}</td>
                <td>{calcResults.enhancedPayment}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RefinanceCalculator.breakeven', {
                    defaultValue: 'Breakeven',
                  })}
                </th>
                <td>
                  {t('RefinanceCalculator.na', {
                    defaultValue: 'N/A',
                  })}
                </td>
                <td>{calcResults.traditionalBreakeven}</td>
                <td>{calcResults.enhancedBreakeven}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('RefinanceCalculator.totalInterest', {
                    defaultValue: 'Total interest',
                  })}
                </th>
                <td>{calcResults.currentInterest}</td>
                <td>{calcResults.traditionalInterest}</td>
                <td>{calcResults.enhancedInterest}</td>
              </tr>
            </tbody>
          </table>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('RefinanceCalculator.graph', {
              defaultValue: 'Graph',
            })}
          </Heading>
          <img
            src={calcResults.chartUrl}
            alt={t('RefinanceCalculator.graphOfResults', {
              defaultValue: 'Graph of results',
            })}
          />
        </div>
      )}
    </div>
  )
}

export { RefinanceCalculator }
export default RefinanceCalculator
